export function matchCardList(raw, options) {
  const normalise = value => value.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[’']/g, '').replace(/\b0+(\d)/g, '$1').replace(/[^a-z0-9/]+/g, ' ').trim();
  const lines = [...new Set(raw.split(/\r?\n/).map(line => line.trim()).filter(Boolean))];
  if (lines.length > 50) return { overflow: true, total: lines.length, matched: 0, ids: [], unmatched: [] };
  const ids = new Set();
  const unmatched = [];
  let matched = 0;
  const titles = options.map(option => ({ id: option.id, words: normalise(option.title).split(/\s+/) }));
  for (const line of lines) {
    const words = normalise(line).split(/\s+/).filter(Boolean);
    const numericOnly = words.length === 1 && /^\d+$/.test(words[0]);
    const hits = words.length ? titles.filter(option => {
      if (numericOnly) {
        const collectorNumbers = option.words.filter(candidate => /^\d+\/\d+$/.test(candidate));
        if (collectorNumbers.length) return collectorNumbers.some(candidate => candidate.startsWith(words[0] + '/'));
      }
      return words.every(word => option.words.some(candidate => candidate === word || (/^\d+$/.test(word) && candidate.startsWith(word + '/'))));
    }) : [];
    if (hits.length) { matched++; hits.forEach(hit => ids.add(hit.id)); } else unmatched.push(line);
  }
  return { overflow: false, total: lines.length, matched, ids: [...ids], unmatched };
}

class MultiCardOrder extends HTMLElement {
  #events;
  #rows = [];
  #busy = false;
  #loaded = false;
  #locked = false;
  #completed = false;
  #format;
  #listIds = null;
  connectedCallback() {
    if (this.#events) return;
    this.#events = new AbortController();
    const { signal } = this.#events;
    this.#format = new Intl.NumberFormat(this.dataset.locale, { style: 'currency', currency: this.dataset.currency, currencyDisplay: 'narrowSymbol' });
    window.addEventListener('pageshow', event => {
      if (!event.persisted || !this.#completed) return;
      this.#completed = false;
      this.#locked = false;
      this.#loaded = false;
      this.querySelectorAll('input,button,textarea').forEach(control => { control.disabled = false; });
      this.#load();
    }, { signal });
    this.querySelector('details').addEventListener('toggle', () => {
      if (this.querySelector('details').open && !this.#loaded) this.#load();
    }, { signal });
    document.addEventListener('click', event => {
      const link = event.target.closest?.('[data-multi-open]');
      if (link?.getAttribute('href') !== '#' + this.id) return;
      event.preventDefault();
      this.querySelector('details').open = true;
      this.scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      this.querySelector('summary').focus({ preventScroll: true });
    }, { signal });
    this.querySelector('[data-multi-wishlist-find]').addEventListener('click', () => this.#findList(), { signal });
    this.querySelector('[data-multi-wishlist-reset]').addEventListener('click', () => {
      this.#listIds = null;
      this.querySelector('[data-multi-wishlist]').value = '';
      this.querySelector('[data-multi-search]').value = '';
      this.querySelector('[data-multi-selected]').checked = false;
      this.querySelector('[data-multi-wishlist-result]').hidden = true;
      this.querySelector('[data-multi-wishlist-status]').textContent = '';
      this.querySelector('[data-multi-wishlist-missing]').hidden = true;
      this.#filter();
    }, { signal });
    this.querySelector('[data-multi-retry]').addEventListener('click', () => this.#load(), { signal });
    this.querySelector('[data-multi-search]').addEventListener('input', () => this.#filter(), { signal });
    this.querySelector('[data-multi-stock]').addEventListener('change', () => this.#filter(), { signal });
    this.querySelector('[data-multi-selected]').addEventListener('change', () => this.#filter(), { signal });
    this.querySelector('[data-multi-list]').addEventListener('input', event => {
      if (!event.target.matches('[data-multi-quantity]')) return;
      event.target.setCustomValidity('');
      this.#total();
    }, { signal });
    this.querySelector('[data-multi-clear]').addEventListener('click', () => {
      this.#rows.forEach(row => { row.querySelector('input').value = '0'; });
      this.#filter();
      this.#total();
    }, { signal });
    this.querySelector('form').addEventListener('submit', event => { event.preventDefault(); this.#submit(); }, { signal });
  }
  disconnectedCallback() { this.#events?.abort(); this.#events = null; }
  async #fetchRows() {
    const url = new URL(this.dataset.url, location.origin);
    url.searchParams.set('view', 'multi-card-data');
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) throw new Error('Unavailable');
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const root = doc.querySelector('[data-multi-data]');
    const rows = [...(root?.querySelectorAll('[data-multi-row]') || [])];
    if (!rows.length || rows.length !== Number(root?.dataset.count) || root.dataset.currency !== this.dataset.currency) throw new Error('Incomplete');
    return rows;
  }
  async #load() {
    if (this.#busy) return;
    this.#busy = true;
    this.querySelector('[data-multi-status]').textContent = this.dataset.loading;
    this.querySelector('[data-multi-retry]').hidden = true;
    try {
      this.#rows = await this.#fetchRows();
      if (!this.isConnected) return;
      this.querySelector('[data-multi-list]').replaceChildren(...this.#rows);
      this.#loaded = true;
      this.querySelector('form').hidden = false;
      this.querySelector('[data-multi-status]').textContent = '';
      this.#filter();
    } catch {
      this.querySelector('[data-multi-status]').textContent = this.dataset.error;
      this.querySelector('[data-multi-retry]').hidden = false;
    } finally { this.#busy = false; this.#total(); }
  }
  #findList() {
    const result = matchCardList(this.querySelector('[data-multi-wishlist]').value, this.#rows.map(row => ({ id: row.dataset.id, title: row.dataset.title })));
    const status = this.querySelector('[data-multi-wishlist-status]');
    const missing = this.querySelector('[data-multi-wishlist-missing]');
    this.querySelector('[data-multi-wishlist-result]').hidden = false;
    if (result.overflow || !result.total) {
      this.#listIds = null;
      missing.querySelector('ul').replaceChildren();
      missing.hidden = true;
      status.textContent = result.overflow ? this.dataset.wishlistLimit : this.dataset.wishlistBlank;
      this.#filter();
      return;
    }
    this.querySelector('.multi-card-order__wishlist').open = false;
    this.#listIds = new Set(result.ids);
    this.querySelector('[data-multi-search]').value = '';
    this.querySelector('[data-multi-selected]').checked = false;
    status.textContent = this.dataset.wishlistReport.replace('__MATCHED__', result.matched).replace('__TOTAL__', result.total);
    missing.querySelector('ul').replaceChildren(...result.unmatched.map(line => { const item = document.createElement('li'); item.textContent = line; return item; }));
    missing.hidden = !result.unmatched.length;
    this.#filter();
  }
  #filter() {
    const words = this.querySelector('[data-multi-search]').value.toLocaleLowerCase().trim().split(/\s+/).filter(Boolean);
    const stock = this.querySelector('[data-multi-stock]').checked;
    const selected = this.querySelector('[data-multi-selected]').checked;
    let shown = 0;
    for (const row of this.#rows) {
      row.hidden = (this.#listIds !== null && !this.#listIds.has(row.dataset.id)) || !words.every(word => row.dataset.title.toLocaleLowerCase().includes(word)) || (stock && row.dataset.available !== 'true') || (selected && Number(row.querySelector('input').value) <= 0);
      if (!row.hidden) shown++;
    }
    this.querySelector('[data-multi-count]').textContent = shown ? this.dataset.count.replace('__COUNT__', shown).replace('__TOTAL__', this.#rows.length) : this.dataset.empty;
  }
  #total() {
    let count = 0, total = 0;
    for (const row of this.#rows) {
      const quantity = Number(row.querySelector('input').value);
      if (Number.isSafeInteger(quantity) && quantity > 0) { count += quantity; total += quantity * Number(row.dataset.price); }
    }
    this.querySelector('[data-multi-total]').textContent = this.#format.format(total / 100);
    this.querySelector('[data-multi-selection]').textContent = (count === 1 ? this.dataset.selectedOne : this.dataset.selected).replace('__COUNT__', count);
    this.querySelector('[data-multi-submit]').disabled = !count || this.#busy || this.#locked;
  }
  async #submit() {
    if (this.#busy || this.#locked) return;
    const invalid = this.#rows.find(row => !row.querySelector('input').checkValidity());
    if (invalid) {
      this.#listIds = null;
      this.querySelector('[data-multi-search]').value = '';
      this.querySelector('[data-multi-stock]').checked = false;
      this.querySelector('[data-multi-selected]').checked = false;
      this.#filter();
      invalid.querySelector('input').reportValidity();
      return;
    }
    const chosen = this.#rows.filter(row => Number(row.querySelector('input').value) > 0);
    if (!chosen.length) return;
    this.#busy = true;
    this.querySelector('[data-multi-submit]').textContent = this.dataset.adding;
    this.querySelector('[data-multi-status]').textContent = '';
    this.querySelectorAll('input,button,textarea').forEach(control => { control.disabled = true; });
    let attempted = false;
    try {
      const latest = new Map((await this.#fetchRows()).map(row => [row.dataset.id, row]));
      let changed = false;
      const items = chosen.map(row => {
        const fresh = latest.get(row.dataset.id);
        const quantity = Number(row.querySelector('input').value);
        if (!fresh || fresh.dataset.available !== 'true' || fresh.dataset.price !== row.dataset.price || quantity > Number(fresh.dataset.max) || quantity < Number(fresh.dataset.min) || quantity % Number(fresh.dataset.step) !== 0) changed = true;
        return { id: Number(row.dataset.id), quantity };
      });
      for (const row of this.#rows) {
        const fresh = latest.get(row.dataset.id);
        if (!fresh) continue;
        Object.assign(row.dataset, fresh.dataset);
        row.querySelector('[data-row-price]').textContent = fresh.querySelector('[data-row-price]').textContent;
        row.querySelector('[data-row-stock]').textContent = fresh.querySelector('[data-row-stock]').textContent;
        row.querySelector('input').max = fresh.dataset.max;
        row.querySelector('input').step = fresh.dataset.step;
      }
      if (changed) { this.querySelector('[data-multi-status]').textContent = this.dataset.changed; return; }
      attempted = true;
      const response = await fetch(this.dataset.addUrl, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items }) });
      if (!response.ok) throw new Error('Cart requires review');
      const result = await response.json();
      if (!Array.isArray(result.items) || result.items.length !== items.length) throw new Error('Cart requires review');
      this.#locked = true;
      this.#completed = true;
      location.assign(this.dataset.cartUrl);
    } catch {
      this.querySelector('[data-multi-status]').textContent = attempted ? this.dataset.uncertain : this.dataset.error;
      if (attempted) { this.#locked = true; this.querySelector('[data-multi-cart]').hidden = false; }
    } finally {
      this.#busy = false;
      this.querySelectorAll('input,button,textarea').forEach(control => { control.disabled = this.#locked; });
      this.#rows.forEach(row => { row.querySelector('input').disabled = this.#locked || row.dataset.available !== 'true'; });
      this.querySelector('[data-multi-submit]').textContent = this.dataset.submit;
      this.#total();
    }
  }
}
if (!customElements.get('multi-card-order')) customElements.define('multi-card-order', MultiCardOrder);
